import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { Search } from "@/components/search/Search.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { ApiTokenEditModal } from "@/pages/settings/api-tokens/widgets/ApiTokenEditModal.tsx";
import { ApiTokenGenerateModal } from "@/pages/settings/api-tokens/widgets/ApiTokenGenerateModal.tsx";
import { useApiUserDelete, useApiUsers } from "@/queries/settings/use-api-users.query.ts";
import { ApiApiUser, ApiApiUserFilter, ApiApiUsersFilterDefault } from "@/types/api-user.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  Stack,
  Table,
  TableCell,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

export function ApiTokens() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderDate } = useFormatter();

  useTitle(t("core:service_users"), t("common:settings"));

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<ApiApiUserFilter>(ApiApiUsersFilterDefault);

  useDebounce(
    () => {
      setFilter((prev) => ({ ...prev, page_number: 1, text: searchTerm }));
    },
    300,
    [searchTerm]
  );

  const { data: apiUsers, isLoading, isFetching } = useApiUsers(filter);
  const apiUserDelete = useApiUserDelete();

  const handleEditOrCreate = useCallback((apiUser?: ApiApiUser) => {
    openModal(ApiTokenEditModal, { apiUser });
  }, []);

  const handleGenerateToken = useCallback((apiUser: ApiApiUser) => {
    openModal(ApiTokenGenerateModal, { apiUser });
  }, []);

  const handleDeleteAction = useCallback((apiUser: ApiApiUser) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("core:service_user_delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await apiUserDelete.mutateAsync(apiUser.id);
        } catch {
          return;
        }
      },
    });
  }, []);

  const columns = useMemo<ColumnDef<ApiApiUser>[]>(
    () => [
      {
        header: t("core:name"),
        cell: (row) => <TableCell>{row.row.original.name}</TableCell>,
        accessorKey: "name",
        ...ColumnSizes.employee,
      },
      {
        header: t("common:created"),
        cell: (row) => (
          <Text className={"text-muted"}>{renderDate(row.row.original.datetime_created)}</Text>
        ),
        accessorKey: "datetime_created",
        ...ColumnSizes.date,
      },
    ],
    [renderDate, t]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiApiUser>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleGenerateToken(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-key"} />}
          title={t("core:api_token_create")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDeleteAction(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDeleteAction, handleEditOrCreate, handleGenerateToken, t]
  );

  return (
    <Page className="h-full">
      <Page.Header showBack={true} title={t("core:service_users")} navigateTo={"/settings"}>
        <Button
          onClick={() => {
            handleEditOrCreate();
          }}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
        >
          {t("common:create_new")}
        </Button>
      </Page.Header>
      <Page.Content stretch>
        <Page.StickyLayout
          toolbar={
            <Stack direction={"horizontal"}>
              <Search
                onInput={(e) => {
                  setSearchTerm(e.currentTarget.value);
                }}
                value={searchTerm}
                type={"text"}
                placeholder={t("common:search_by_name")}
                className={"w-96"}
              />
            </Stack>
          }
          footer={
            <PaginationBlock
              metadata={apiUsers?.metadata}
              onPageChange={(page) => {
                setFilter({ ...filter, page_number: page });
              }}
            />
          }
        >
          <Table
            stickyHeader={true}
            renderRowAction={renderRowAction}
            data={apiUsers?.items || []}
            columns={columns}
            isFetching={isFetching || apiUserDelete.isPending}
            showSkeleton={isLoading}
            showNotFoundError={filter.text != "" && apiUsers?.metadata.total_items == 0}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={<EmptyState />}
          />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
}
